import { gql } from "lib/apolloClient";
import { studentsAlertFragment } from "modules/CommonFragments";

export const staffFragment = {
  basicStaffDetails: gql`
    fragment staffItem on Staff {
      id
      firstName
      middleName
      lastName
      preferredName
      prefix
      suffix
      pronouns
      profileImage
      email
      type
      showPitchScreen
      role
      showClassSelectionScreen
      showBifurcationScreen
      isDevUser
      isArchived
      entityTags {
        id
        key
        value
      }
    }
  `,
};

export const organizationStudentFeedFragment = {
  organizationStudentFeedEdge: gql`
    fragment organizationStudentFeedEdgeItem on Organization {
      id
      students(
        first: $first
        after: $after
        filters: $filters
        orderBy: $orderBy
        orderByDirection: $orderByDirection
      ) {
        totalCount
        edges {
          node {
            id
            firstName
            middleName
            lastName
            pronouns
            preferredName
            prefix
            suffix
            profileImage
            sourceId
            isArchived
            isArchivedV2(filters: { academicYearId: $academicYearId })
            registrationCategory
            gender
            email
            yearGroup {
              id
              name
              grade {
                id
                name
              }
            }
            families(filters: $familiesFilter) {
              id
              status
              familyMember {
                id
                lastName
                firstName
                middleName
                preferredName
                prefix
                suffix
                pronouns
                email
              }
              createdAt
              lastUpdatedAt
            }
            alertTypes {
              ...alertTypeFields
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
    ${studentsAlertFragment}
  `,
};

export const organizationStaffFeedFragment = {
  organizationStaffFeedEdge: gql`
    fragment organizationStaffFeedEdgeItem on Organization {
      id
      staff(
        first: $first
        after: $after
        filters: $filters
        orderBy: $orderBy
        orderByDirection: $orderByDirection
      ) {
        userCount {
          role
          totalCount
        }
        edges {
          node {
            id
            ... on Staff {
              id
              profileImage
              firstName
              lastName
              middleName
              preferredName
              prefix
              suffix
              pronouns
              email
              role
              isArchived
              isArchivedV2(filters: { academicYearId: $academicYearId })
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  `,
};

export const organizationCourseFeedFragment = {
  organizationCourseFeedEdge: gql`
    fragment organizationCourseFeedEdgeItem on Organization {
      id
      courses(
        first: $first
        after: $after
        filters: $filters
        orderBy: $orderBy
        orderByDirection: $orderByDirection
      ) {
        totalCount
        edges {
          node {
            id
            title
            sourcedId
            profileImage
            isArchived
            academicYears {
              id
              startDate
              endDate
            }
            genericTags {
              id
              label
              type
            }
            grades {
              id
              name
            }
            subjects {
              id
              name
              variants {
                level {
                  id
                  label
                  type
                }
              }
            }
            learningCourse {
              id
              title
              isArchived
              __typename
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  `,
};

export const subTaskDataFragment = {
  subTask: gql`
    fragment subTaskData on IntegrationSubTask {
      id
      parsedData
      type
      status
      hasError
    }
  `,
};

export const subTaskFeedFragment = {
  subTaskFeedEdge: gql`
    fragment subTaskFeedEdgeItem on IntegrationJob {
      id
      subTasksFeed(first: $first, filters: $filters, afterV2: $afterV2) {
        edges {
          node {
            ...subTaskData
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
        totalCount
      }
    }
    ${subTaskDataFragment.subTask}
  `,
};

export const jobDataFragment = {
  jobData: gql`
    fragment jobData on IntegrationJob {
      id
      status
      serviceName
      createdAt
      curriculum
      curriculumId
      type
      metadata
      subTasksFeed(filters: $subTaskFilters) @include(if: $fetchSubTasksCount) {
        totalCount
        errorCount
      }
      createdBy {
        ...staffItem
      }
    }
    ${staffFragment.basicStaffDetails}
  `,
};

export const jobFeedFragment = {
  organizationJobFeedEdge: gql`
    fragment organizationCourseFeedEdgeItem on Organization {
      id
      jobFeed(
        first: $first
        afterV2: $afterV2
        curriculumProgramId: $curriculumProgramId
        filters: $filters
        academicYearState: $academicYearState
      ) {
        totalCount
        edges {
          node {
            ...jobData
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
    ${jobDataFragment.jobData}
  `,
};

export const learningCourseFeedFragment = gql`
  fragment learningCourseItem on LearningCourse {
    id
    type
    title
    state
    code
    isArchived
    displayName
    displayMetadata
    classes @skip(if: $excludeLearningCourseRelations) {
      id
      title
    }
    subjects @skip(if: $excludeLearningCourseRelations) {
      id
      name
    }
    grades @skip(if: $excludeLearningCourseRelations) {
      id
      name
    }
    createdBy @skip(if: $excludeLearningCourseRelations) {
      id
      type
      firstName
      middleName
      lastName
      profileImage
    }
    collaborators(filters: $collaboratorFilters)
      @skip(if: $excludeLearningCourseRelations) {
      totalCount
      edges {
        node {
          id
          type
          firstName
          middleName
          lastName
          profileImage
        }
      }
    }
  }
`;
